import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { Container } from "@mui/system";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import MySnackbar from "../MySnackbar";
import { Context } from "../..";
import useSnackbar from "../../hooks/useSnackbar";
import useUnsavedChangesWarning from "../../hooks/useUnsavedChangesWarning";
import { getBranches } from "../../http/clinicAPI";
import { createClinicService, fetchOneService, deleteClinicService } from "../../http/clinicServiceAPI";
import { fetchServiceCategories } from "../../http/categoryAPI";
import { fetchDoctors } from "../../http/doctorAPI";

const API_URL = process.env.REACT_APP_API_URL || window.API_URL;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 250,
    },
  },
};

const CreateUpdateService = () => {
  const [serviceName, setServiceName] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [image, setImage] = useState(null);
  const [serviceImage, setServiceImage] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchesNames, setSelectedBranchesNames] = useState([]);
  const [service, setService] = useState({});
  const [branchError, setBranchError] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState([]);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const snack = useSnackbar(false, "Ошибка");
  const { user } = useContext(Context);
  const { confirmLeave } = useUnsavedChangesWarning(hasUnsaved);
  const isNew = params.id === "new";
  const pageTitle = isNew ? "Создание услуги" : "Редактирование услуги";

  useEffect(() => {
    fetchBranches();
    fetchCategories();
    fetchDoctorList();
    if (params.id !== "new") fetchServiceData(params.id);
  }, [params.id]);

  const fetchBranches = async () => {
    try {
      const data = await getBranches();
      setBranches(data);
    } catch (error) {
      snack.handleChange(true, "Ошибка при загрузке филиалов.");
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await fetchServiceCategories();
      setCategories(data);
    } catch (error) {
      snack.handleChange(true, "Ошибка при загрузке категорий.");
    }
  };

  const fetchDoctorList = async () => {
    try {
      const data = await fetchDoctors();
      setDoctors(data);
    } catch (error) {
      snack.handleChange(true, "Ошибка при загрузке врачей.");
    }
  };

  const fetchServiceData = async (id) => {
    try {
      const data = await fetchOneService(id);
      setService(data);
      setServiceName(data.name);
      setServiceDescription(data.description || '');
      setSelectedBranchesNames(data.branches?.map((branchData) => branchData['branch.name']));
      setSelectedCategoryIds(data.categoryIds || []);
      setSelectedDoctorIds(data.doctorIds || []);
      setImage(API_URL + "/static/" + data.image || null);
      setHasUnsaved(false);
    } catch (error) {
      snack.handleChange(true, "Ошибка при загрузке данных услуги.");
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        const img = new Image();
        img.src = e.target.result;
        img.onload = function () {
          const { width, height } = img;
          if (width > 2560 || height > 2560) {
            snack.handleChange(true, "Стороны изображения должны быть менее 2560px");
          } else {
            setImage(URL.createObjectURL(file));
            setServiceImage(file);
            setHasUnsaved(true);
          }
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectBranch = (event) => {
    const { value } = event.target;
    setSelectedBranchesNames(
      typeof value === "string" ? value.split(",") : value
    );
    setBranchError(value.length === 0); // Проверка на выбор филиалов
    setHasUnsaved(true);
  };

  const deleteSelectedBranch = (branchName) => {
    setSelectedBranchesNames(
      selectedBranchesNames.filter((branch) => branch !== branchName)
    );
    setBranchError(selectedBranchesNames.length === 1); // Проверка на выбор филиалов после удаления
    setHasUnsaved(true);
  };

  const handleSelectCategories = (event) => {
    const { value } = event.target;
    setSelectedCategoryIds(typeof value === "string" ? value.split(",") : value);
    setHasUnsaved(true);
  };

  const deleteSelectedCategory = (categoryId) => {
    setSelectedCategoryIds(selectedCategoryIds.filter((id) => id !== categoryId));
    setHasUnsaved(true);
  };

  const handleSelectDoctors = (event) => {
    const { value } = event.target;
    setSelectedDoctorIds(typeof value === "string" ? value.split(",") : value);
    setHasUnsaved(true);
  };

  const deleteSelectedDoctor = (doctorId) => {
    setSelectedDoctorIds(selectedDoctorIds.filter((id) => id !== doctorId));
    setHasUnsaved(true);
  };

  const save = async ({ status }) => {
    if (selectedBranchesNames.length === 0) {
      setBranchError(true); // Если филиалы не выбраны, показываем ошибку
      return;
    }

    const formData = new FormData();
    formData.append("name", serviceName);
    formData.append("description", serviceDescription);
    formData.append("clinicId", user.user.clinicId);
    formData.append("authorId", user.user.id);
    formData.append("status", status);

    const serviceBranches = selectedBranchesNames.map((selectedBranch) =>
      branches.find((branch) => branch.name === selectedBranch)
    );

    if (serviceBranches) formData.append("branches", JSON.stringify(serviceBranches));
    else formData.append("branches", JSON.stringify([]));

    if (serviceImage) formData.append("image", serviceImage);

    // Всегда отправляем categoryIds при редактировании, даже если массив пустой
    // Это нужно для очистки категорий
    if (params.id !== "new" || (selectedCategoryIds && selectedCategoryIds.length > 0)) {
      formData.append("categoryIds", JSON.stringify(selectedCategoryIds || []));
    }

    // Всегда отправляем doctorIds при редактировании, даже если массив пустой
    if (params.id !== "new" || (selectedDoctorIds && selectedDoctorIds.length > 0)) {
      formData.append("doctorIds", JSON.stringify(selectedDoctorIds || []));
    }

    if (params.id !== "new") formData.append("id", params.id);

    try {
      await createClinicService(formData);
      if (status === 0)
        snack.handleChange(false, "Услуга отправлена в архив.");
      else if (status === 1)
        snack.handleChange(false, "Услуга сохранена.");
      setHasUnsaved(false);
      navigate("/examples");
    } catch (error) {
      snack.handleChange(true, "Ошибка при сохранении услуги.");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Вы уверены, что хотите удалить эту услугу?")) {
      try {
        await deleteClinicService(params.id);
        snack.handleChange(false, "Услуга удалена.");
        navigate("/examples");
      } catch (error) {
        snack.handleChange(true, "Ошибка при удалении услуги.");
      }
    }
  };

  const handleBack = () => {
    if (!confirmLeave()) {
      return;
    }
    navigate(-1);
  };

  return (
    <Container sx={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
      <MySnackbar {...snack} />

      <Stack spacing={1} sx={{ mb: 3, width: "100%" }}>
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIcon />}
          sx={{ alignSelf: "flex-start" }}
          onClick={handleBack}
        >
          Назад
        </Button>
        <Typography variant="h4" fontWeight={600}>
          {pageTitle}
        </Typography>
        {!isNew && (
          <Typography variant="body2" component={"span"} color="templateText.secondary">
            Статус:
            <Chip
              size="small"
              color={service.status === 1 ? "primary" : "warning"}
              label={service.status === 1 ? "Сохранено" : "Архивированно"}
              sx={{ ml: 0.5 }}
            />
          </Typography>
        )}
      </Stack>

      <Box sx={{ maxWidth: 500, margin: "0 auto", padding: 3 }}>

        <form>
          <TextField
            label="Название услуги"
            variant="outlined"
            fullWidth
            value={serviceName}
            onChange={(e) => {
              setServiceName(e.target.value);
              setHasUnsaved(true);
            }}
            margin="normal"
          />

          <TextField
            label="Описание услуги"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={serviceDescription}
            onChange={(e) => {
              setServiceDescription(e.target.value);
              setHasUnsaved(true);
            }}
            margin="normal"
          />

          <FormControl sx={{ mb: 1, width: "100%" }} error={branchError}>
            <InputLabel id="branchesLabel">Филиалы, в которых отображается данная услуга</InputLabel>
            <Select
            labelId="branchesLabel"
            multiple
            fullWidth
            value={selectedBranchesNames}
            onChange={handleSelectBranch}
            input={<OutlinedInput id="select-multiple-chip" label="Филиалы, в которых отображается данная услуга" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((branchName) => (
                  <Chip
                    key={branchName}
                    deleteIcon={
                      <CancelIcon
                        onMouseDown={(event) => event.stopPropagation()}
                      />
                    }
                    label={
                      branches?.find((branch) => branch.name === branchName)
                        ?.name
                    }
                    onDelete={(e) => deleteSelectedBranch(branchName)}
                    clickable
                  />
                ))}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {branches
              .filter((branch) => branch.isActive !== false)
              .map((branch) => (
                <MenuItem key={branch.id} value={branch.name}>
                  {branch.name}
                </MenuItem>
              ))}
          </Select>
            {branchError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Пожалуйста, выберите хотя бы один филиал.
              </Typography>
            )}
          </FormControl>

          <FormControl sx={{ mb: 1, width: "100%" }}>
            <InputLabel id="categoriesLabel">Категории, в которых отображается данная услуга</InputLabel>
            <Select
              labelId="categoriesLabel"
              multiple
              fullWidth
              value={selectedCategoryIds}
              onChange={handleSelectCategories}
              input={<OutlinedInput id="select-multiple-chip" label="Категории, в которых отображается данная услуга" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((categoryId) => {
                    const category = categories?.find((cat) => cat.id === categoryId);
                    return category ? (
                      <Chip
                        key={categoryId}
                        label={category.name}
                        deleteIcon={
                          <CancelIcon onMouseDown={(event) => event.stopPropagation()} />
                        }
                        onDelete={(e) => deleteSelectedCategory(categoryId)}
                        clickable
                      />
                    ) : null;
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ mb: 1, width: "100%" }}>
            <InputLabel id="doctorsLabel">Врачи, которые отображаются в данной услуге</InputLabel>
            <Select
              labelId="doctorsLabel"
              multiple
              fullWidth
              value={selectedDoctorIds}
              onChange={handleSelectDoctors}
              input={<OutlinedInput id="select-multiple-chip" label="Врачи, которые отображаются в данной услуге" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((doctorId) => {
                    const doctor = doctors?.find((doc) => doc.id === doctorId);
                    return doctor ? (
                      <Chip
                        key={doctorId}
                        label={doctor.name}
                        deleteIcon={
                          <CancelIcon onMouseDown={(event) => event.stopPropagation()} />
                        }
                        onDelete={(e) => deleteSelectedDoctor(doctorId)}
                        clickable
                      />
                    ) : null;
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {doctors.map((doctor) => (
                <MenuItem key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ marginBottom: 2 }}>
            <Button variant="contained" component="label" startIcon={<CloudUploadIcon />} fullWidth>
              Загрузить картинку
              <input type="file" hidden accept="image/*" onChange={handleImageChange} />
            </Button>
            {image && (
              <Box sx={{ marginTop: 2 }}>
                <img src={image} alt="Preview" style={{ width: "100%", height: "auto", borderRadius: 4 }} />
              </Box>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                onClick={() => save({ status: 1 })}
                disabled={!serviceName || selectedBranchesNames.length === 0}
                fullWidth
              >
                Сохранить
              </Button>
            </Grid>
            {params.id !== "new" && (
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  fullWidth
                  startIcon={<DeleteIcon />}
                >
                  Удалить
                </Button>
              </Grid>
            )}
          </Grid>
        </form>
      </Box>
    </Container>
  );
};

export default CreateUpdateService;
